import { catchError, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { AuthService } from '../utility-services/auth.service';
import { AlertService } from '../utility-services/alert.service';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { ValidationErrorModel } from '@ose/models';

@Injectable({ providedIn: 'root' })
export class ErrorInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const cookieService = inject(CookieService);
    const authService = inject(AuthService);
    const alertService = inject(AlertService);
    const router = inject(Router);
    const translate = inject(TranslateService);

    if (request.url.includes('.json')) {
      request = request.clone({ url: `${request.url}?v=1.43` });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const status = error.status;

        if (status === 400) {
          const validations =
            (error.error?.validations as ValidationErrorModel[]) || [];
          if (validations.length > 0) {
            const messages = validations.map((err) => {
              let msg = translate.instant(err.tag, {
                field: translate.instant(toPascalCase(err.field)),
              });
              if (!msg || msg === err.tag) msg = `${err.tag} ${err.field}`;
              return msg + '.';
            });
            alertService.showToast({
              body: messages.join('\n'),
              type: 'danger',
            });
          } else {
            const msg =
              (error.error as { message?: string })?.message ?? 'Unknown error';
            alertService.showToast({
              body: translate.instant(msg),
              type: 'danger',
            });
          }
          return throwError(() => error.error);
        }

        if (status > 0) {
          let msg = 'Unknown error';

          if (error.error && typeof error.error === 'object') {
            const apiError = error.error as {
              code?: number;
              message?: string;
              details?: string[];
            };
            if (apiError?.message) {
              const translated = translate.instant(apiError.message);
              msg =
                translated && translated !== apiError.message
                  ? translated
                  : apiError.message;
            }
            if (apiError?.details?.length) {
              msg += ' - ' + apiError.details.join(', ');
            }
          } else if (
            typeof error.error === 'string' &&
            error.error.trim().length > 0
          ) {
            msg = translate.instant(error.error);
          } else if (error.statusText) {
            msg = error.statusText;
          } else {
            msg = `Error ${status}`;
          }
          if (status === 401) {
            if (
              request.url.includes('member/v1/users') ||
              request.url.includes('member/v1/admins') ||
              request.url.includes('member/v1/profiles/current/phones/single')
            ) {
              return of(
                new HttpResponse({
                  body: { ...error.error, status: 201 },
                })
              );
            }
            if (authService.isTokenExpired && authService.hasRefreshToken) {
              authService.refreshToken();
            } else {
              sessionStorage.clear();
              cookieService.deleteAll();
              void router.navigate(['/auth', 'sign-in']);
            }
          }
          if (!excludedToast.includes(request.url)) {
            alertService.showToast({ body: msg, type: 'danger' });
          }
        }

        return throwError(() => error.error ?? { status });
      })
    );
  }
}

export function toPascalCase(str: string): string {
  return str
    .split('_')
    .map(
      (word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()
    )
    .join('');
}

const excludedToast = ['/finance/v1/prices/last'];
